// @ts-check
// Lista das campanhas de e-mail (/campaigns/email; spec/email.md seção 19.6; contrato 09 de e-mail, seção 16): busca e
// filtro de situação na URL, tabela no computador e cartões no celular, menu de ações de cada linha e atualização a
// cada 15 s enquanto alguma prepara ou envia, com a aba visível. "Nova campanha" cria o rascunho e abre o assistente.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Link, navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import {
  backToDraftEmailCampaign, cancelEmailCampaign, createEmailCampaign, deleteEmailCampaign, duplicateEmailCampaign, listEmailCampaigns, pauseEmailCampaign,
  unscheduleEmailCampaign,
} from '../../data/email.js';
import { fieldsById, viewsState } from '../../data/metadata-store.js';
import { summarizeAudience } from '../../lib/audience-summary.js';
import {
  EMAIL_CAMPAIGN_LIST_FILTERS, displayedDelivered, emailCampaignActions, emailCampaignHref, emailCampaignStatusTone, filterEmailCampaigns, formatRate, listRefreshMilliseconds,
  hasUnmeasuredEngagement, readListFilter,
} from '../../lib/email-campaigns.js';
import { formatDateTime, formatNumber } from '../../lib/format.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, MenuItem } from '../../ui/dropdown.js';
import { MenuKeyboardScope } from '../../ui/menu-keyboard.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { usePolling } from '../conversations/conversations-state.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { ResumeEmailCampaignDialog } from './campaign-resume-dialog.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/email.js').EmailCampaign} EmailCampaign
 * @typedef {import('../../lib/email-campaigns.js').EmailCampaignAction} EmailCampaignAction
 */

/** @param {number} rate */
const percent = (rate) => formatNumber(rate, { percentage: true, decimals: 1 });

/** Selo da situação (textos comuns aos dois canais, K30). @param {{ status: string, isLegacy?: boolean }} props */
export function EmailCampaignStatusBadge({ status, isLegacy = false }) {
  return html`<span class="ec-badges">
    <span class="ec-badge" data-tone=${emailCampaignStatusTone(status)} data-status=${status}>${t(`campaignStatuses.statuses.${status}`)}</span>
    ${isLegacy ? html`<span class="ec-badge" data-tone="gray">${t('emailCampaigns.legacyBadge')}</span>` : null}
  </span>`;
}

/** Data que a coluna Envio mostra. @param {EmailCampaign} campaign */
function sendDateText(campaign) {
  if (campaign.status === 'SCHEDULED' && campaign.scheduledAt) return t('emailCampaigns.scheduledShort', { date: formatDateTime(campaign.scheduledAt) });
  const date = campaign.sentAt ?? campaign.startedAt;
  return date ? formatDateTime(date) : t('emailCampaigns.notSent');
}

/** Público em linguagem simples: rascunho e agendada pelas condições, enviada pelos leads preparados. @param {EmailCampaign} campaign */
export function audienceText(campaign) {
  if (campaign.progress?.totalCards !== null && campaign.progress?.totalCards !== undefined && campaign.status !== 'DRAFT' && campaign.status !== 'SCHEDULED') {
    return t('emailCampaigns.audienceLeads', { count: campaign.progress.totalCards });
  }
  if (!campaign.audienceDefinition) return '';
  const phrases = summarizeAudience(campaign.audienceDefinition, { getField: (id) => fieldsById.value.get(id), views: viewsState.value, maximumConditions: 1 });
  return phrases.slice(0, 2).join(' · ');
}

/**
 * Menu de três pontos e as confirmações de uma campanha (lista e página).
 * @param {{ campaign: EmailCampaign, onChanged: (campaign: EmailCampaign | null) => void, exclude?: EmailCampaignAction[] }} props
 */
export function EmailCampaignActionsMenu({ campaign, onChanged, exclude = [] }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [confirm, setConfirm] = useState(/** @type {'cancel' | 'delete' | 'unschedule' | 'resume' | null} */ (null));
  const [isBusy, setIsBusy] = useState(false);
  const actions = emailCampaignActions(campaign).filter((action) => !exclude.includes(action));

  /** @param {() => Promise<EmailCampaign | null>} run @param {string} message */
  const execute = async (run, message) => {
    setIsBusy(true);
    try {
      const result = await run();
      showSuccessSnackbar(message);
      setConfirm(null);
      onChanged(result);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
    }
  };

  /** @param {EmailCampaignAction} action */
  const choose = async (action) => {
    setIsOpen(false);
    if (action === 'open' || action === 'report') navigate(emailCampaignHref(campaign.id));
    else if (action === 'duplicate') {
      await execute(async () => {
        const copy = await duplicateEmailCampaign(campaign.id);
        navigate(emailCampaignHref(copy.id));
        return copy;
      }, t('emailCampaigns.toasts.duplicated'));
    } else if (action === 'pause') await execute(() => pauseEmailCampaign(campaign.id), t('emailCampaigns.toasts.paused'));
    else if (action === 'backToDraft') await execute(() => backToDraftEmailCampaign(campaign.id), t('emailCampaigns.toasts.backToDraft'));
    else setConfirm(/** @type {any} */ (action));
  };

  const icons = /** @type {Record<EmailCampaignAction, string>} */ ({
    open: 'IconExternalLink', report: 'IconChartBar', duplicate: 'IconCopy', unschedule: 'IconCalendarX', pause: 'IconPlayerPause', resume: 'IconPlayerPlay',
    cancel: 'IconPlayerStop', delete: 'IconTrash', backToDraft: 'IconArrowBackUp',
  });
  return html`<span class="ec-actions-menu">
    <${LightIconButton} icon="IconDotsVertical" ariaLabel=${t('emailCampaigns.actions.more')} data-testid="email-campaign-menu"
      onClick=${(/** @type {MouseEvent} */ event) => {
        event.preventDefault();
        event.stopPropagation();
        anchorRef.current = /** @type {HTMLButtonElement} */ (event.currentTarget);
        setIsOpen((open) => !open);
      }} />
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-end" width=${220} role="menu">
      <${DropdownMenuItems}>
        <${MenuKeyboardScope} isOpen=${isOpen} anchorRef=${anchorRef} onClose=${() => setIsOpen(false)}>
          ${actions.map((action) => html`<${MenuItem} key=${action} text=${t(`emailCampaigns.actions.${action}`)} icon=${icons[action]}
            accent=${action === 'delete' || action === 'cancel' ? 'danger' : 'default'} onClick=${() => choose(action)} />`)}
        <//>
      <//>
    <//>
    ${confirm === 'cancel'
      ? html`<${Modal} title=${t('emailCampaigns.confirm.cancelTitle', { name: campaign.name })} subtitle=${t('emailCampaigns.confirm.cancelDescription')}
          confirmLabel=${t('emailCampaigns.actions.cancel')} confirmAccent="danger" isLoading=${isBusy} onClose=${() => setConfirm(null)}
          onConfirm=${() => execute(() => cancelEmailCampaign(campaign.id), t('emailCampaigns.toasts.canceled'))} />`
      : null}
    ${confirm === 'delete'
      ? html`<${Modal} title=${t('emailCampaigns.confirm.deleteTitle', { name: campaign.name })} subtitle=${t('emailCampaigns.confirm.deleteDescription')}
          confirmLabel=${t('emailCampaigns.actions.delete')} confirmAccent="danger" isLoading=${isBusy} onClose=${() => setConfirm(null)}
          onConfirm=${() => execute(async () => {
            await deleteEmailCampaign(campaign.id);
            return null;
          }, t('emailCampaigns.toasts.deleted'))} />`
      : null}
    ${confirm === 'unschedule'
      ? html`<${Modal} title=${t('emailCampaigns.confirm.unscheduleTitle', { name: campaign.name })} subtitle=${t('emailCampaigns.confirm.unscheduleDescription')}
          confirmLabel=${t('emailCampaigns.actions.unschedule')} isLoading=${isBusy} onClose=${() => setConfirm(null)}
          onConfirm=${() => execute(() => unscheduleEmailCampaign(campaign.id), t('emailCampaigns.toasts.unscheduled'))} />`
      : null}
    ${confirm === 'resume'
      ? html`<${ResumeEmailCampaignDialog} campaign=${campaign} onClose=${() => setConfirm(null)}
          onResumed=${(/** @type {EmailCampaign} */ resumed) => {
            setConfirm(null);
            onChanged(resumed);
          }} />`
      : null}
  </span>`;
}

/** Linha da tabela (computador). @param {{ campaign: EmailCampaign, onChanged: (campaign: EmailCampaign | null) => void }} props */
function CampaignRow({ campaign, onChanged }) {
  const { counts, rates } = campaign;
  const isDraft = campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED';
  const unmeasured = isDraft || hasUnmeasuredEngagement(campaign);
  const number = (/** @type {number} */ value) => (isDraft ? '-' : formatNumber(value));
  return html`<div class="ec-table-row" role="row" data-testid="email-campaign-row">
    <span role="cell" class="ec-cell-name"><${Link} href=${emailCampaignHref(campaign.id)} className="ec-link">${campaign.name}<//></span>
    <span role="cell"><${EmailCampaignStatusBadge} status=${campaign.status} isLegacy=${campaign.formatVersion === 1} /></span>
    <span role="cell" class="ec-muted">${sendDateText(campaign)}</span>
    <span role="cell" class="ec-muted ec-cell-audience" title=${audienceText(campaign)}>${audienceText(campaign)}</span>
    <span role="cell" class="ec-number">${unmeasured ? '-' : formatNumber(displayedDelivered(campaign))}</span>
    <span role="cell" class="ec-number">${unmeasured ? '-' : formatRate(rates.openRate, percent)}</span>
    <span role="cell" class="ec-number">${unmeasured ? '-' : formatRate(rates.clickRate, percent)}</span>
    <span role="cell" class="ec-number">${number(counts.unsubscribed)}</span>
    <span role="cell" class="ec-number">${number(counts.bounced)}</span>
    <span role="cell" class="ec-cell-actions"><${EmailCampaignActionsMenu} campaign=${campaign} onChanged=${onChanged} /></span>
  </div>`;
}

/** Cartão (celular). @param {{ campaign: EmailCampaign, onChanged: (campaign: EmailCampaign | null) => void }} props */
function CampaignCard({ campaign, onChanged }) {
  const isDraft = campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED';
  return html`<article class="ec-card" data-testid="email-campaign-card">
    <div class="ec-card-header">
      <${Link} href=${emailCampaignHref(campaign.id)} className="ec-link ec-card-name">${campaign.name}<//>
      <${EmailCampaignActionsMenu} campaign=${campaign} onChanged=${onChanged} />
    </div>
    <div class="ec-card-meta">
      <${EmailCampaignStatusBadge} status=${campaign.status} isLegacy=${campaign.formatVersion === 1} />
      <span class="ec-muted">${sendDateText(campaign)}</span>
    </div>
    ${isDraft
      ? null
      : html`<div class="ec-card-rates">
          <span>${t('emailCampaigns.rateLabels.delivery', { rate: hasUnmeasuredEngagement(campaign) ? '-' : formatRate(campaign.rates.deliveryRate, percent) })}</span>
          <span>${t('emailCampaigns.rateLabels.open', { rate: formatRate(campaign.rates.openRate, percent) })}</span>
          <span>${t('emailCampaigns.rateLabels.click', { rate: formatRate(campaign.rates.clickRate, percent) })}</span>
        </div>`}
  </article>`;
}

export function CampaignsPage() {
  const isMobile = useIsMobile();
  const [campaigns, setCampaigns] = useState(/** @type {EmailCampaign[] | null} */ (null));
  const [failed, setFailed] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const parameters = searchParamsState.value;
  const filter = readListFilter(parameters.get('status'));
  const search = parameters.get('search') ?? '';

  const load = () => listEmailCampaigns()
    .then((list) => {
      setCampaigns(list);
      setFailed(false);
    })
    .catch(() => setFailed(true));

  useEffect(() => {
    document.title = t('emailCampaigns.title');
    load();
  }, []);
  const interval = listRefreshMilliseconds(campaigns ?? []);
  usePolling(load, interval ?? 15_000, interval !== null);

  const create = async () => {
    setIsCreating(true);
    try {
      const campaign = await createEmailCampaign({ name: t('emailCampaigns.newName') });
      navigate(emailCampaignHref(campaign.id));
    } catch (error) {
      showErrorSnackbar(error);
      setIsCreating(false);
    }
  };

  /** @param {EmailCampaign | null} changed */
  const onChanged = (changed) => {
    if (!changed) {
      load();
      return;
    }
    setCampaigns((current) => (current ?? []).map((item) => (item.id === changed.id ? { ...item, ...changed } : item)));
    load();
  };

  const visible = campaigns ? filterEmailCampaigns(campaigns, { filter, search }) : [];
  const newButton = html`<${Button} title=${t('emailCampaigns.newCampaign')} icon="IconPlus" accent="blue" size="small" isLoading=${isCreating} onClick=${create}
    data-testid="email-campaign-new" />`;

  /** @returns {import('preact').ComponentChildren} */
  const body = () => {
    if (failed && !campaigns) {
      return html`<div class="ec-inline-error" role="alert"><span>${t('emailCampaigns.loadError')}</span>
        <${Button} title=${t('emailCampaigns.retry')} size="small" variant="secondary" onClick=${load} /></div>`;
    }
    if (!campaigns) return html`<div class="ec-skeletons">${Array.from({ length: 4 }, (_, index) => html`<${Skeleton} key=${index} height=${48} radius=${8} />`)}</div>`;
    if (campaigns.length === 0) {
      return html`<${EmptyState} icon="IconMail" title=${t('emailCampaigns.emptyTitle')} subtitle=${t('emailCampaigns.emptyDescription')}
        action=${html`<${Button} title=${t('emailCampaigns.emptyAction')} icon="IconPlus" variant="secondary" size="small" onClick=${create} />`} />`;
    }
    if (visible.length === 0) return html`<p class="ec-empty-filter">${t('emailCampaigns.noResults')}</p>`;
    if (isMobile) return html`<div class="ec-cards">${visible.map((campaign) => html`<${CampaignCard} key=${campaign.id} campaign=${campaign} onChanged=${onChanged} />`)}</div>`;
    return html`<div class="ec-table" role="table" aria-label=${t('emailCampaigns.title')}>
      <div class="ec-table-row ec-table-header" role="row">
        ${['name', 'status', 'sentAt', 'audience', 'delivered', 'openRate', 'clickRate', 'unsubscribed', 'bounced'].map((column) => html`<span role="columnheader"
          class=${['delivered', 'openRate', 'clickRate', 'unsubscribed', 'bounced'].includes(column) ? 'ec-number' : ''}>${t(`emailCampaigns.columns.${column}`)}</span>`)}
        <span role="columnheader"><span class="visually-hidden-text">${t('emailCampaigns.columns.actions')}</span></span>
      </div>
      ${visible.map((campaign) => html`<${CampaignRow} key=${campaign.id} campaign=${campaign} onChanged=${onChanged} />`)}
    </div>`;
  };

  return html`<${PageCard} header=${html`<${PageHeader} icon="IconMail" title=${t('emailCampaigns.title')} actions=${newButton} />`}>
    <div class="ec-page" data-testid="email-campaigns-page">
      <p class="ec-intro"><${Icon} name="IconShieldLock" size=${16} /><span>${t('emailCampaigns.intro')}</span></p>
      <div class="ec-toolbar">
        <label class="ec-search">
          <${Icon} name="IconSearch" size=${14} />
          <input type="search" value=${search} placeholder=${t('emailCampaigns.search')} aria-label=${t('emailCampaigns.search')}
            onInput=${(/** @type {Event} */ event) => updateSearchParams({ search: /** @type {HTMLInputElement} */ (event.currentTarget).value || null })} />
        </label>
        <div class="ec-filter-chips" role="radiogroup" aria-label=${t('emailCampaigns.statusFilter')}>
          ${EMAIL_CAMPAIGN_LIST_FILTERS.map((option) => html`<button key=${option} type="button" role="radio" class="ec-filter-chip"
            aria-checked=${filter === option ? 'true' : 'false'} onClick=${() => updateSearchParams({ status: option === 'ALL' ? null : option })}>
            ${t(`emailCampaigns.filters.${option}`)}
          </button>`)}
        </div>
      </div>
      ${body()}
    </div>
  <//>`;
}
